import React from 'react'

import { Button, ButtonProps } from '../../../components/button/button.js'
import { Card } from '../../../components/card/card.js'
import { CheckCard, CheckCardProps } from '../../../components/forms/check-card/check-card.js'
import { Checkbox, CheckboxProps } from '../../../components/forms/checkbox/checkbox.js'
import { Datepicker, DatepickerProps } from '../../../components/forms/datepicker/datepicker.js'
import { Select, SelectProps } from '../../../components/forms/select/select.js'
import { Textarea, TextareaProps } from '../../../components/forms/textarea/textarea.js'
import { GridColumn } from '../../../components/grid/grid-column.js'
import { Grid } from '../../../components/grid/grid.js'
import { useCurrentBreakPoint } from '../../../components/helper/hooks/use-current-breakpoint.js'
import { NotificationCard, NotificationCardProps } from '../../../components/notification-card/notification-card.js'
import { PageTitle, PageTitleProps } from '../../../components/page-title/page-title.js'
import { Separator } from '../../../components/separator/separator.js'
import { SubscriptionCard, SubscriptionCardProps } from '../../../components/subscription-card/subscription-card.js'

export interface SwapSummaryItem {
    title: string
    values?: string[]
    subValues?: Array<{
        title: string
        value?: string
        list?: string[]
    }>
}

export interface ClaimDamageAdditionalProps {
    datepicker?: DatepickerProps
    textarea?: TextareaProps
    specificDamage?: CheckCardProps[]
    title?: string
    damageTitle?: string
}

export interface ViewSwapProps {
    button: ButtonProps
    subscription?: SubscriptionCardProps

    title: PageTitleProps
    formTitle: string
    conditionTerms?: NotificationCardProps
    damageProps?: {
        title: string
        damageSelect: SelectProps
        additional?: ClaimDamageAdditionalProps
    }
    notifications?: NotificationCardProps[]
    termsCheckbox?: CheckboxProps
    showSummary?: boolean
    summary?: {
        items: SwapSummaryItem[]
    }
    isBolttech?: boolean
}

export const SummaryCard = (props: { small?: boolean } & Partial<ViewSwapProps>): JSX.Element => {
    const bp = useCurrentBreakPoint()
    const isMobile = bp === 'xs'

    return (
        <div className="text">
            {props.summary?.items?.map((item, index) => (
                <div key={`item-${index}`}>
                    {props.small && (
                        <h5 key={`item-title-small-${index}`} className={isMobile ? 'text-small bold' : 'h5 bold'}>
                            {item.title}
                        </h5>
                    )}

                    {!props.small && (
                        <h3 key={`item-title-${index}`} className={isMobile ? 'text-small bold' : 'h3'}>
                            {item.title}
                        </h3>
                    )}

                    {item.values?.map((value, subIndex) => (
                        <p key={`summary-item-${index}-${subIndex}`} className="text-small text__margin-small">
                            {value}
                        </p>
                    ))}

                    {item.subValues?.map((subValue, subIndex) => (
                        <div key={`summary-item-${index}-${subIndex}`}>
                            <p className="text-small text__margin-small bold">{subValue.title}</p>
                            {subValue.value && <p className="text-small text__margin-small">{subValue.value}</p>}
                            {subValue.list && (
                                <ul className="text-small text__margin-small list">
                                    {subValue.list.map((value, grandSubIndex) => (
                                        <li
                                            dangerouslySetInnerHTML={{
                                                __html: value,
                                            }}
                                            key={`summary-item-${index}-${subIndex}-${grandSubIndex}`}
                                        ></li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}

                    <Separator key={`item-sep-${index}`} variant="large" />
                </div>
            ))}
        </div>
    )
}

export const ViewSwap = (props: ViewSwapProps): JSX.Element => {
    const bp = useCurrentBreakPoint()
    const isMobile = bp === 'xs'

    const renderSteps = () => (
        <Grid className="text__margin-double">
            {props.subscription && (
                <GridColumn width={['md-6', 'xl-4']}>
                    <Grid>
                        <GridColumn>
                            <SubscriptionCard {...props.subscription} />
                        </GridColumn>
                        {props.conditionTerms && (
                            <GridColumn>
                                <NotificationCard {...props.conditionTerms} />
                            </GridColumn>
                        )}
                    </Grid>
                </GridColumn>
            )}

            <GridColumn width={['md-6', 'xl-8']}>
                <Grid>
                    <GridColumn>
                        <h2 className={isMobile ? 'text-large bold' : 'h2'}>{props.formTitle}</h2>
                    </GridColumn>
                    <GridColumn>
                        <Card hasContent={true} padding="large" variant="quinary">
                            <Grid>
                                {!isMobile && (
                                    <GridColumn>
                                        <h3>{props.damageProps!.title}</h3>
                                    </GridColumn>
                                )}

                                <GridColumn>
                                    <Select {...props.damageProps!.damageSelect} bordered={true} />
                                </GridColumn>

                                {props.damageProps?.additional && !props.isBolttech && (
                                    <>
                                        {props.damageProps.additional.title && (
                                            <GridColumn>
                                                <p className="text-small">{props.damageProps.additional.title}</p>
                                            </GridColumn>
                                        )}
                                        {props.damageProps.additional.datepicker && (
                                            <GridColumn>
                                                <Datepicker
                                                    {...props.damageProps.additional.datepicker}
                                                    bordered={true}
                                                />
                                            </GridColumn>
                                        )}
                                        {props.damageProps.additional.textarea && (
                                            <GridColumn>
                                                <Textarea
                                                    {...props.damageProps.additional.textarea}
                                                    bordered={true}
                                                    hiddenLabel={true}
                                                />
                                            </GridColumn>
                                        )}
                                        {props.damageProps.additional.damageTitle && (
                                            <GridColumn>
                                                <h4 className={isMobile ? 'text-small bold' : 'h3'}>
                                                    {props.damageProps.additional.damageTitle}
                                                </h4>
                                            </GridColumn>
                                        )}
                                        {props.damageProps.additional.specificDamage && (
                                            <GridColumn>
                                                <Grid>
                                                    {props.damageProps.additional.specificDamage.map(
                                                        (checkCard, index) => (
                                                            <GridColumn width={['xs-12']} key={`grid-check-${index}`}>
                                                                <CheckCard {...checkCard} />
                                                            </GridColumn>
                                                        ),
                                                    )}
                                                </Grid>
                                            </GridColumn>
                                        )}
                                    </>
                                )}
                            </Grid>
                        </Card>
                    </GridColumn>
                    {props.notifications?.map((notification, index) => (
                        <GridColumn key={index}>
                            <NotificationCard {...notification} />
                        </GridColumn>
                    ))}
                    <GridColumn>
                        <Button {...props.button} isBlock="xs" />
                    </GridColumn>
                </Grid>
            </GridColumn>
        </Grid>
    )

    const renderSummary = () => (
        <Grid align={['center-xs']} className="text__margin-double">
            <GridColumn width={['lg-10', 'xl-8']}>
                <Grid>
                    <GridColumn>
                        <h2 className={isMobile ? 'text-large bold' : 'h2'}>{props.formTitle}</h2>
                    </GridColumn>
                    <GridColumn>
                        <Card hasContent={true} padding="large" variant="quinary">
                            <SummaryCard {...props} />
                        </Card>
                    </GridColumn>
                    {props.termsCheckbox && (
                        <GridColumn>
                            <Checkbox {...props.termsCheckbox} />
                        </GridColumn>
                    )}
                    <GridColumn>
                        <Button {...props.button} isBlock="xs" />
                    </GridColumn>
                </Grid>
            </GridColumn>
        </Grid>
    )

    return (
        <div className="text">
            <Grid align={['middle-xs']} noWrap={true}>
                <GridColumn width={['max-md']}>
                    <PageTitle {...props.title} alwaysButton={true} />
                </GridColumn>
            </Grid>
            {props.showSummary && renderSummary()}
            {props.damageProps && renderSteps()}
        </div>
    )
}
